import type { AgentCardKind } from './index.js';
import { opIdOfToolName } from './routes.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * KART GÖSTERİM POLİTİKASI — kart ne zaman ÇİZİLİR (Faz 8 son entegrasyon,
 * 1 Eki 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Sorun: Faz 8 dalga 2'den sonra özel kartı olmayan her okuma `records`
 * kartına, liste okuyan her işlem `picker` kartına düşüyordu. Ajan kendi
 * muhakemesi için on şey okursa sohbete on kart düşüyordu — ürün sahibinin
 * kaldırttığı araç izi karmaşası başka biçimde geri geliyordu.
 *
 * KURAL: kart yalnız ajan kullanıcıya bir şey GÖSTERMEK ya da SEÇTİRMEK
 * istediğinde çıkar; iç okumalar kartsız. Ajan bunu araç girdisinde açıkça
 * söyler: kart üretebilen her OKUMA aracının girdisinde isteğe bağlı
 * `present` (boolean) alanı var — ekleyen ve ayıklayan araç sarmalayıcısı
 * (`agent.tools.ts` şemaya ekler; `agent-op-tool.service.ts`,
 * `agent-app-tool.service.ts`, çekirdek/operatör servisleri çıkarır). Katalog
 * işlemlerinin op şemaları DEĞİŞMEZ.
 *
 * `present` verilmezse varsayılan TÜRÜN sınıfından (+ araç istisnası):
 *
 *   always      her zaman görünür, `present:false` bile gizlemez — ajanın
 *               kullanıcıdan bir şey istediği kartlar: soru (`ask`), bağlantı
 *               (`connect`), dosya isteği (`upload`), "senden bir adım
 *               gerekiyor" (`needs_action`), AI'ın ürettiği dosya (`file`,
 *               Faz 9). Onay kartları (öneriler) bu birliğin dışında ve
 *               zaten her zaman görünür.
 *   shown       varsayılan GÖRÜNÜR — sunum kartları (metrik, sıralama, kırılım,
 *               yorum, lead, plan…). İç okumada ajan `present:false` verir.
 *   pick        varsayılan GİZLİ — seçici kartlar (`picker`, ürün/kreatif
 *               seçicisi, lead formu). Yalnız SEÇTİRMEK için: `present:true`.
 *   on_request  varsayılan GİZLİ — genel `records` tablosu. `present:true`.
 *
 * YAZMA araçlarının kartı (not kaydı, takip görevi, dışa aktarma…) işin
 * sonucu: alan yok, her zaman görünür. Başarısız işlemin kartı (`needs_action`,
 * `connect`) her zaman görünür.
 *
 * Aynı turda aynı türden birden çok kart varsa yalnız SONUNCUSU çizilir
 * (web `tool-renderers.tsx` → `AgentCards`; anahtar görünümün `single`ı ya da
 * tür). `always` türleri bu kısıttan muaf — kendi `single` anahtarları var.
 *
 * Gizlenen kart KAYDA yazılmıyor (`ui: null`): kart her sohbet açılışında
 * iniyor ve hiç çizilmeyecek bir kartın 64 KB'lık yükü yalnız bedel olurdu.
 * Model sonucu her durumda AYNEN görür.
 */

/** Araç girdisindeki ortak alanın adı. */
export const CARD_PRESENT_FIELD = 'present' as const;

export type CardPresentation = 'always' | 'shown' | 'pick' | 'on_request';

/**
 * Türün varsayılan sınıfı. TAM tipli: birliğe yeni tür eklenip buraya
 * yazılmazsa paket derlenmez (gösterim kararı sessizce "görünür" olmasın).
 */
export const CARD_PRESENTATION: Readonly<Record<AgentCardKind, CardPresentation>> = {
  /* ── ajan kullanıcıdan bir şey istiyor ── */
  ask: 'always',
  connect: 'always',
  upload: 'always',
  needs_action: 'always',
  /* Faz 9 X1 — AI'ın ürettiği dosya: kullanıcı istedi, kart işin kendisi. */
  file: 'always',
  /* ── seçtirmek için ── */
  picker: 'pick',
  product: 'pick',
  creative: 'pick',
  lead_form: 'pick',
  /* Logo adayları — kullanıcıya SEÇTİRİLİYOR (aracı istisnası aşağıda). */
  logo_candidates: 'pick',
  /* ── genel tablo ── */
  records: 'on_request',
  /* ── sunum ── */
  metrics: 'shown',
  table: 'shown',
  breakdown: 'shown',
  ranking: 'shown',
  insight: 'shown',
  forecast: 'shown',
  budget_split: 'shown',
  sales: 'shown',
  compare: 'shown',
  recommendations: 'shown',
  experiment: 'shown',
  campaign_plan: 'shown',
  audience: 'shown',
  keywords: 'shown',
  schedule: 'shown',
  ad_preview: 'shown',
  creative_job: 'shown',
  /* Faz 8a — reklam videosu: canlı ilerleme + o anki kapının arayüzü. Sunum
     kartı; ajan kendi kontrolü için okursa `present:false`. */
  production: 'shown',
  /* Dalga 5 — marka kiti + AI notları; varyant A/B özeti. Sunum kartları;
     ajan kendi muhakemesi için okursa (kiti üretimden önce yoklamak)
     `present:false`. */
  brand_kit: 'shown',
  variant_performance: 'shown',
  creative_performance: 'shown',
  competitor_ads: 'shown',
  catalog_health: 'shown',
  automation_rule: 'shown',
  tasks: 'shown',
  activity: 'shown',
  approvals: 'shown',
  export: 'shown',
  balance: 'shown',
  connections_overview: 'shown',
  disconnect_impact: 'shown',
  sync_status: 'shown',
  account_status: 'shown',
  team: 'shown',
  memory: 'shown',
  comments: 'shown',
  leads: 'shown',
  policy_issue: 'shown',
  tracking_health: 'shown',
};

/**
 * Araç istisnaları — türü sunum kartı ama çoğunlukla ajanın KENDİ muhakemesi
 * için çağrılan okumalar (kampanya kimliği bulmak, notlara bakmak, rolü
 * yoklamak, genel rapor sorgusu) varsayılan gizli; tek kaydı gösteren okuma
 * (`creative_get`) seçici türünde olsa da varsayılan görünür. Katalog işlemi
 * `op:<opId>` anahtarıyla.
 */
const TOOL_PRESENTATION: Readonly<Record<string, CardPresentation>> = {
  get_campaigns: 'on_request',
  list_notes: 'on_request',
  list_tasks: 'on_request',
  workspace_context: 'on_request',
  /* Çekirdek bağlantı/kredi okuması çoğunlukla ajanın KENDİ kontrolü (hesap
     kimliği, bakiye) — kartı yalnız `present: true` ile (1 Eki 2026). */
  get_connections: 'on_request',
  get_credit_balance: 'on_request',
  creative_get: 'shown',
  /* Tek üreticisi bu araç ve işi adayları GÖSTERMEK; ajan kendi seçecekse
     `present:false` verir (1 Eki 2026). */
  brand_logo_from_platform: 'shown',
  'op:meta.insights.query': 'on_request',
  'op:meta.insights.report_get': 'on_request',
  'op:tiktok.report.run': 'on_request',
  'op:x.report.job_status': 'on_request',
};

function toolKey(tool: string): string {
  const opId = opIdOfToolName(tool);
  return opId ? `op:${opId}` : tool;
}

/** Bu aracın bu türdeki kartının sınıfı (`always` araç istisnasıyla değişmez). */
export function cardPresentationOf(tool: string, kind: AgentCardKind): CardPresentation {
  const base = CARD_PRESENTATION[kind] ?? 'shown';
  if (base === 'always') return base;
  return TOOL_PRESENTATION[toolKey(tool)] ?? base;
}

/** Tür her zaman görünür mü (`present` yok sayılır; tur içi tekilleştirmeden muaf). */
export function isAlwaysVisibleCard(kind: AgentCardKind): boolean {
  return CARD_PRESENTATION[kind] === 'always';
}

/**
 * Kart çizilsin mi.
 *
 * @param source  `read` — okuma aracının sonucu (`present` geçerli);
 *                `write` — yazma aracının sonucu kartı (işin kendisi, görünür);
 *                `failure` — başarısız işlemin kartı (görünür).
 * @param present modelin girdisindeki değer; yoksa `undefined`.
 */
export function isCardVisible(input: {
  tool: string;
  kind: AgentCardKind;
  present: boolean | undefined;
  source: 'read' | 'write' | 'failure';
}): boolean {
  const presentation = cardPresentationOf(input.tool, input.kind);
  if (presentation === 'always') return true;
  if (input.source !== 'read') return true;
  if (input.present === true) return true;
  if (input.present === false) return false;
  return presentation === 'shown';
}

/**
 * OKUMA aracının `present` alanının varsayılanı — şema açıklaması bunu
 * söylüyor. `kinds` aracın çizebileceği türler (`toolCardKinds`); boşsa
 * yalnız genel `records` kartı. Yalnız `always` türleri çizen araçta alan
 * YOK (`null`) — o kart zaten her zaman görünür.
 */
export function presentDefaultOf(tool: string, kinds: readonly AgentCardKind[]): { shown: boolean; pick: boolean } | null {
  const candidates: readonly AgentCardKind[] = kinds.length > 0 ? kinds : ['records'];
  const kind = candidates.find((item) => !isAlwaysVisibleCard(item));
  if (!kind) return null;
  const presentation = cardPresentationOf(tool, kind);
  return { shown: presentation === 'shown', pick: presentation === 'pick' };
}

/**
 * Modelin girdisinden `present`i okur — üst düzeyde ya da tek anahtarlı bir
 * zarfın içinde (`{ parameter: { …, present: true } }`, modelin bazen yaptığı
 * sarma; hafıza: llm-arac-argumani-zarfta-gelebilir). Boolean değilse
 * `undefined` (metin "true" kabul EDİLMİYOR — belirsiz değer varsayılana düşer).
 */
export function readCardPresent(input: unknown): boolean | undefined {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return undefined;
  const record = input as Record<string, unknown>;
  const direct = record[CARD_PRESENT_FIELD];
  if (typeof direct === 'boolean') return direct;
  const keys = Object.keys(record);
  if (keys.length === 1) {
    const inner = record[keys[0] as string];
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      const nested = (inner as Record<string, unknown>)[CARD_PRESENT_FIELD];
      if (typeof nested === 'boolean') return nested;
    }
  }
  return undefined;
}

/**
 * Girdiden ortak alanı ÇIKARIR (üst düzey + tek anahtarlı zarf) — aracın kendi
 * şemasına `present` hiç ulaşmaz (katı şema reddetmesin, katalog işlemi
 * platforma taşımasın). Girdi nesne değilse aynen döner.
 */
export function stripCardPresent<T>(input: T): T {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return input;
  const record = input as unknown as Record<string, unknown>;
  let out: Record<string, unknown> = record;
  if (CARD_PRESENT_FIELD in record) {
    const { [CARD_PRESENT_FIELD]: _present, ...rest } = record;
    void _present;
    out = rest;
  }
  const keys = Object.keys(out);
  if (keys.length === 1) {
    const key = keys[0] as string;
    const inner = out[key];
    if (inner && typeof inner === 'object' && !Array.isArray(inner) && CARD_PRESENT_FIELD in (inner as object)) {
      const { [CARD_PRESENT_FIELD]: _nested, ...innerRest } = inner as Record<string, unknown>;
      void _nested;
      out = { ...out, [key]: innerRest };
    }
  }
  return out as unknown as T;
}
